import { formatAddressPartsAsLine, parseAddressLine } from "./parseAddressLine.js";
export const SMARTY_SUGGEST_INPUT_MIN_CHARS = 2;
export const SMARTY_EMPTY_TITLE = "No address suggestions found.";
export const SMARTY_EMPTY_HINT = "Try a different format — street, city, and state usually works best.";
export const SMARTY_EMPTY_CLASS = "nm-smarty-ac-empty";
export function smartyEmptyStateCss(emptyClass = SMARTY_EMPTY_CLASS) {
  return `
    .${emptyClass} {
      padding: 10px 12px;
      color: var(--nm-neutral-secondary-text);
      text-align: left;
      cursor: default;
      pointer-events: none;
    }
    .${emptyClass}__title {
      font-size: 14px;
      line-height: 1.35;
      color: var(--nm-neutral-secondary-text);
    }
    .${emptyClass}__hint {
      margin-top: 4px;
      font-size: 13px;
      line-height: 1.4;
      color: var(--nm-neutral-secondary-text);
      opacity: 0.85;
    }
  `;
}
export function appendSmartyEmptyState(dd, {
  className = SMARTY_EMPTY_CLASS
} = {}) {
  if (!dd || typeof document === "undefined") return null;
  const el = document.createElement("div");
  el.className = className;
  const title = document.createElement("div");
  title.className = `${className}__title`;
  title.textContent = SMARTY_EMPTY_TITLE;
  const hint = document.createElement("div");
  hint.className = `${className}__hint`;
  hint.textContent = SMARTY_EMPTY_HINT;
  el.appendChild(title);
  el.appendChild(hint);
  dd.appendChild(el);
  return el;
}
export function suggestSearchFromInputValue(raw) {
  const t = String(raw || "").trim();
  if (!t) return "";
  if (t.includes(",")) {
    const p = parseAddressLine(t);
    const st = (p.street || "").trim();
    if (st.length >= SMARTY_SUGGEST_INPUT_MIN_CHARS) return st.slice(0, 32);
    const first = t.split(",")[0]?.trim() || "";
    if (first.length >= SMARTY_SUGGEST_INPUT_MIN_CHARS) return first.slice(0, 32);
  }
  return t.slice(0, 32);
}
export function smartySuggestionEntries(s) {
  return Number(s?.entries) || 0;
}

/** Multi-unit suggestions that need a Smarty `selected` expand (entries > 1 with secondary). */
export function smartyIsMultiEntry(s) {
  return smartySuggestionEntries(s) > 1 && Boolean(String(s?.secondary || "").trim());
}

export function smartyMultiEntryLinkLabel(s) {
  const n = smartySuggestionEntries(s);
  if (n <= 0) return "";
  return n === 1 ? "1 address >" : `${n} addresses >`;
}

export function smartyDisplayLine(s) {
  if (!s) return "";
  const secondary = String(s.secondary || "").trim();
  const street = [String(s.street_line || "").trim(), secondary].filter(Boolean).join(" ").trim();
  const city = String(s.city || "").trim();
  const state = String(s.state || "").trim().slice(0, 2);
  const zip = String(s.zipcode || "").trim();
  return formatAddressPartsAsLine({
    street,
    city,
    state,
    zip
  });
}

/** Pull suite/apt/unit out of a street line when Smarty embeds it. */
export function splitStreetAndSecondary(streetRaw, secondaryRaw = "") {
  const secondaryIn = String(secondaryRaw || "").trim();
  let street = String(streetRaw || "").trim();
  if (secondaryIn) {
    return {
      street,
      secondary: secondaryIn
    };
  }
  const m = street.match(/^(.*?)\s+(#|APT\.?|APARTMENT|STE\.?|SUITE|UNIT|FL\.?|FLOOR|RM\.?|ROOM)\s*(.+)$/i);
  if (m) {
    const label = String(m[2] || "").replace(/\./g, "").toUpperCase();
    const unit = String(m[3] || "").trim();
    return {
      street: String(m[1] || "").trim(),
      secondary: label === "#" ? `# ${unit}` : `${label} ${unit}`.trim()
    };
  }
  return {
    street,
    secondary: ""
  };
}

/** Layout CSS for “N addresses >” on multi-entry rows. */
export function smartyItemEntriesLinkCss(itemClass) {
  return `
    .${itemClass}__main {
      flex: 1;
      min-width: 0;
      white-space: normal;
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .${itemClass}__entries {
      flex-shrink: 0;
      align-self: center;
      color: var(--nm-brand, rgb(var(--blue-9, 39 129 246)));
      font-size: 0.8125rem;
      font-weight: 500;
      white-space: nowrap;
      line-height: 1.25rem;
    }
    .${itemClass}:hover .${itemClass}__entries,
    .${itemClass}:focus .${itemClass}__entries {
      text-decoration: underline;
    }
  `;
}

/** Populate a suggestion button; multi-entry rows get a right-side “N addresses >” link cue. */
export function fillSmartySuggestionButton(btn, s, { itemClass } = {}) {
  if (!btn) return;
  const mainClass = itemClass ? `${itemClass}__main` : "nm-smarty-ac-item__main";
  const entriesClass = itemClass ? `${itemClass}__entries` : "nm-smarty-ac-item__entries";
  btn.textContent = "";
  const main = document.createElement("span");
  main.className = mainClass;
  main.textContent = smartyDisplayLine(s);
  btn.appendChild(main);
  if (smartyIsMultiEntry(s)) {
    const link = document.createElement("span");
    link.className = entriesClass;
    link.textContent = smartyMultiEntryLinkLabel(s);
    btn.appendChild(link);
  }
}
export function commitPayloadFromVerify(res, s) {
  let streetRaw = "";
  let secondaryRaw = "";
  let city = "";
  let state = "";
  let zip = "";
  if (res?.ok && res.components) {
    const c = res.components;
    streetRaw = String(c.street || "").trim();
    secondaryRaw = String(c.secondary || "").trim();
    city = String(c.city || "").trim();
    state = String(c.state || "").trim().slice(0, 2);
    zip = String(c.zipcode || "").trim();
  } else {
    streetRaw = String(s?.street_line || "").trim();
    secondaryRaw = String(s?.secondary || "").trim();
    city = String(s?.city || "").trim();
    state = String(s?.state || "").trim().slice(0, 2);
    zip = String(s?.zipcode || "").trim();
  }
  const {
    street,
    secondary
  } = splitStreetAndSecondary(streetRaw, secondaryRaw);
  const formattedLine = formatAddressPartsAsLine({
    street,
    city,
    state,
    zip
  });
  return {
    formattedLine,
    street,
    secondary,
    apt: secondary,
    city,
    state,
    zip
  };
}
